/**
 * @module components/Settings/SettingsModal
 * @description Modal de configuración del sistema con pestañas por área.
 *
 * Agrupa todas las configuraciones del sistema en un modal centralizado con pestañas:
 * - **Perfil**: nombre, email, unidad, avatar del usuario
 * - **Preferencias**: tema claro/oscuro, idioma, densidad de la UI
 * - **Notificaciones**: configuración de alertas in-app y email
 * - **IA**: API key de Gemini/OpenAI, configuración del asistente
 * - **Email**: configuración del servidor SMTP para notificaciones
 *
 * Solo la pestaña de Email está restringida a administradores.
 *
 * @see ProfileTab para la configuración del perfil de usuario
 * @see AITab para la configuración del asistente IA
 * @see NotificationsTab para las preferencias de notificaciones
 */
import React, { useState, useRef } from 'react';
import { X, User, Bell, Palette, Shield, Sparkles, Mail } from 'lucide-react';
import { ProfileTab } from './ProfileTab';
import { PreferencesTab } from './PreferencesTab';
import { NotificationsTab } from './NotificationsTab';
import { AITab } from './AITab';
import { EmailSettingsTab } from './EmailSettingsTab';

interface SettingsModalProps {
  onClose: () => void;
}

type TabType = 'profile' | 'preferences' | 'notifications' | 'email' | 'ai';

export function SettingsModal({ onClose }: SettingsModalProps) {
  const [activeTab, setActiveTab] = useState<TabType>('profile');
  const [isSaving, setIsSaving] = useState(false);
  const profileTabRef = useRef<{ saveChanges: () => Promise<void> }>(null);
  const preferencesTabRef = useRef<{ saveChanges: () => Promise<void> }>(null);
  const notificationsTabRef = useRef<{ saveChanges: () => Promise<void> }>(null);
  const emailTabRef = useRef<{ saveChanges: () => Promise<void> }>(null);
  const aiTabRef = useRef<{ saveChanges: () => Promise<void> }>(null);
  const handleSaveAll = async () => {
    setIsSaving(true);
    try {
      // Guardar cambios de todos los tabs
      if (activeTab === 'profile' && profileTabRef.current) {
        await profileTabRef.current.saveChanges();
      } else if (activeTab === 'preferences' && preferencesTabRef.current) {
        await preferencesTabRef.current.saveChanges();
      } else if (activeTab === 'notifications' && notificationsTabRef.current) {
        await notificationsTabRef.current.saveChanges();
      } else if (activeTab === 'email' && emailTabRef.current) {
        await emailTabRef.current.saveChanges();
      } else if (activeTab === 'ai' && aiTabRef.current) {
        await aiTabRef.current.saveChanges();
      }

      // Pequeño delay para mostrar el estado de éxito
      await new Promise(resolve => setTimeout(resolve, 500));
    } catch (err) {
      console.error('Error guardando cambios:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const tabs = [
    { id: 'profile' as TabType, label: 'Perfil', icon: <User className="w-4 h-4" /> },
    { id: 'preferences' as TabType, label: 'Preferencias', icon: <Palette className="w-4 h-4" /> },
    { id: 'notifications' as TabType, label: 'Notificaciones', icon: <Bell className="w-4 h-4" /> },
    { id: 'email' as TabType, label: 'Email', icon: <Mail className="w-4 h-4" /> },
    { id: 'ai' as TabType, label: 'Asistente IA', icon: <Sparkles className="w-4 h-4" /> },
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-4xl w-full max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <Shield className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              Configuración
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Personaliza tu experiencia en el sistema
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            aria-label="Cerrar"
          >
            <X className="w-5 h-5 text-gray-600 dark:text-gray-300" />
          </button>
        </div>

        <div className="flex flex-1 overflow-hidden">
          {/* Sidebar con tabs */}
          <div className="w-48 border-r border-gray-200 dark:border-gray-700 p-4">
            <nav className="space-y-1">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    activeTab === tab.id
                      ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                >
                  {tab.icon}
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>

          {/* Content area */}
          <div className="flex-1 overflow-auto p-6">
            {activeTab === 'profile' && <ProfileTab ref={profileTabRef} />}
            {activeTab === 'preferences' && <PreferencesTab ref={preferencesTabRef} />}
            {activeTab === 'notifications' && <NotificationsTab ref={notificationsTabRef} />}
            {activeTab === 'email' && <EmailSettingsTab ref={emailTabRef} />}
            {activeTab === 'ai' && <AITab ref={aiTabRef} />}
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            Cancelar
          </button>
          <button
            onClick={handleSaveAll}
            disabled={isSaving}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isSaving ? (
              <>
                <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Guardando...
              </>
            ) : (
              'Guardar Cambios'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
